import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { Panel, PanelHeader, PanelBody, PageHeader } from '../components/ui/Panel';
import Button from '../components/ui/Button';
import { SkeletonRows, ErrorNotice, InfoNotice } from '../components/ui/States';
import { dateShort } from '../lib/format';
import { canMutate } from '../lib/roles';
import { useAuth } from '../contexts/AuthContext';
import { errorMessage, inputClass, selectClass } from '../lib/programs';

interface Cell {
  value: number | string | null;
  people: number | null;
  source: string;
  note: string | null;
  gap: string | null;
}
interface CountTable {
  title: string;
  columns: { key: string; label: string }[];
  rows: { key: string; label: string; cells: Record<string, Cell> }[];
}
interface DetailColumn {
  key: string;
  label: string;
  source: string;
  tally: Record<string, number>;
  gap: string | null;
}
interface DetailTable {
  title: string;
  columns: DetailColumn[];
  rows: { number: number; retired?: boolean; values: Record<string, string | number | null> }[];
  people: number;
}
interface Report {
  year: number;
  yearLabel: string;
  rules: {
    cohortRule: string;
    nonCohortRule: string;
    tenureYears: number;
    teamGroups: string[];
    platformLedSources: string[];
  };
  tabs: { A: CountTable; B: CountTable; C?: DetailTable; D: CountTable };
  gaps: { tab: string; row: string; column: string; message: string }[];
  notes: string[];
  unmappedRoles: { value: string; people: number }[];
}
interface Snapshot {
  id: string;
  year: number;
  codeVersion: string | null;
  generatedAt: string;
  submittedFileSha256: string | null;
  submittedFileName: string | null;
  note: string | null;
}
interface FillSummary {
  cells: number;
  tables: { table: string; sheet: string; cells: number; rows?: number; clearedRows?: number }[];
  unfilled: { table: string; ref: string; row: string; column: string; reason: string }[];
  warnings: string[];
}

type TabKey = 'A' | 'B' | 'C' | 'D';
const TAB_LABEL: Record<TabKey, string> = {
  A: 'A · Platform team',
  B: 'B · Participant totals',
  C: 'C · Participant details',
  D: 'D · Experiential learning',
};
const FIRST_YEAR = 2022;

const toBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

function download(name: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

const showValue = (v: number | string | null) =>
  v === null ? '—' : typeof v === 'string' ? (v.length > 60 ? `${v.slice(0, 60)}…` : v) : v.toLocaleString('en-CA');

function Explanation({ title, cell, onClose }: { title: string; cell: Cell; onClose: () => void }) {
  return (
    <div className="border border-line-strong bg-white p-4 mb-4">
      <div className="flex justify-between gap-4">
        <div className="text-small text-ink-2">{title}</div>
        <button className="text-small underline" onClick={onClose}>
          Close
        </button>
      </div>
      <div className="font-semibold text-[26px] mt-1 whitespace-pre-line">
        {cell.value === null ? 'Not filled' : typeof cell.value === 'string' ? '' : cell.value.toLocaleString('en-CA')}
      </div>
      {typeof cell.value === 'string' ? <p className="text-table whitespace-pre-line mt-1">{cell.value}</p> : null}
      <p className="text-table mt-2">
        <span className="text-ink-2">How it is counted: </span>
        {cell.source}
      </p>
      {cell.people !== null ? (
        <p className="text-table mt-1">
          <span className="text-ink-2">Distinct people behind it: </span>
          {cell.people}
        </p>
      ) : null}
      {cell.note ? <p className="text-small text-ink-2 mt-1">{cell.note}</p> : null}
      {cell.gap === 'not_tracked' ? (
        <p className="text-small mt-2 text-ink bg-[#fff4d6] px-2 py-1 inline-block">
          Not tracked on the platform: the template cell is left as it is.
        </p>
      ) : null}
    </div>
  );
}

function CountView({ table }: { table: CountTable }) {
  const [open, setOpen] = useState<{ title: string; cell: Cell } | null>(null);
  return (
    <>
      {open ? <Explanation title={open.title} cell={open.cell} onClose={() => setOpen(null)} /> : null}
      <table className="w-full">
        <thead>
          <tr className="text-left text-small text-ink-2 border-b border-line-strong">
            <th className="py-2 pr-4">Row</th>
            {table.columns.map((c) => (
              <th key={c.key} className="py-2 pr-4 text-right">
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((r) => (
            <tr key={r.key} className="border-b border-line text-table align-top">
              <td className="py-[9px] pr-4">{r.label}</td>
              {table.columns.map((c) => {
                const cell = r.cells[c.key];
                if (!cell) return <td key={c.key} />;
                const gap = cell.gap === 'not_tracked';
                return (
                  <td key={c.key} className={`py-[9px] pr-4 text-right tabular-nums ${gap ? 'bg-[#fff4d6]' : ''}`}>
                    <button
                      className="underline decoration-dotted text-right"
                      title={cell.source}
                      onClick={() => setOpen({ title: `${r.label} · ${c.label}`, cell })}
                    >
                      {gap ? 'not tracked' : showValue(cell.value)}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-small text-ink-2 mt-3">Click a cell for how it is counted. Exact counts, as CIHR asks.</p>
    </>
  );
}

function DetailView({ table }: { table: DetailTable }) {
  const [col, setCol] = useState<DetailColumn | null>(null);
  return (
    <>
      {col ? (
        <div className="border border-line-strong bg-white p-4 mb-4">
          <div className="flex justify-between gap-4">
            <div className="text-small text-ink-2">{col.label}</div>
            <button className="text-small underline" onClick={() => setCol(null)}>
              Close
            </button>
          </div>
          <p className="text-table mt-2">
            <span className="text-ink-2">Source: </span>
            {col.source}
          </p>
          <p className="text-table mt-1">
            <span className="text-ink-2">Values: </span>
            {Object.entries(col.tally)
              .map(([v, n]) => `${v}: ${n}`)
              .join(' · ')}
          </p>
        </div>
      ) : null}
      <div className="overflow-x-auto">
        <table className="w-max min-w-full">
          <thead>
            <tr className="text-left text-small text-ink-2 border-b border-line-strong">
              {table.columns.map((c) => (
                <th key={c.key} className={`py-2 pr-3 align-bottom max-w-[120px] ${c.gap ? 'bg-[#fff4d6]' : ''}`}>
                  <button className="underline decoration-dotted text-left" onClick={() => setCol(c)}>
                    {c.label}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((r) => (
              <tr key={r.number} className={`border-b border-line text-small ${r.retired ? 'text-ink-2' : ''}`}>
                {table.columns.map((c) => {
                  const v = r.values[c.key];
                  return (
                    <td
                      key={c.key}
                      className={`py-[7px] pr-3 whitespace-nowrap ${v === 'Unknown' ? 'text-ink-2' : ''}`}
                    >
                      {v === null || v === undefined ? '' : String(v)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-small text-ink-2 mt-3">
        One row per cohort participant since {FIRST_YEAR}. Numbers are kept for good: a merged or deleted record keeps
        its row with a comment, and nobody is ever renumbered.
      </p>
    </>
  );
}

function TemplatePanel({ year, yearLabel }: { year: number; yearLabel: string }) {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const fill = useMutation(
    async () => {
      if (!file) throw new Error('Pick a file');
      const content = await toBase64(file);
      const res = await api.post(
        `/reports/cihr/${year}/template`,
        { fileName: file.name, content },
        { timeout: 120000 },
      );
      const data = res.data.data as { fileName: string; content: string; summary: FillSummary };
      const bytes = Uint8Array.from(atob(data.content), (ch) => ch.charCodeAt(0));
      download(
        data.fileName,
        new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      );
      return data.summary;
    },
    { onSuccess: () => queryClient.invalidateQueries(['cihr', year]) },
  );
  return (
    <Panel>
      <PanelHeader title="Fill CIHR's template" />
      <PanelBody>
        <p className="text-table text-ink-2 mb-3">
          Upload this year&apos;s template as CIHR sent it (.xlsx). Cells are found by their row and column labels, so a
          template whose layout changed still fills. Cells the platform cannot count are left as they are and listed.
        </p>
        <input
          type="file"
          accept=".xlsx"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          className="text-small"
        />
        <div className="mt-3">
          <Button variant="primary" size="sm" disabled={!file || fill.isLoading} onClick={() => fill.mutate()}>
            {fill.isLoading ? 'Filling…' : `Fill for ${yearLabel} and download`}
          </Button>
        </div>
        {fill.error ? (
          <div className="mt-3">
            <ErrorNotice>{errorMessage(fill.error, 'Could not fill the template.')}</ErrorNotice>
          </div>
        ) : null}
        {fill.data ? (
          <div className="mt-4 text-small">
            <p>
              {fill.data.cells} cells written:{' '}
              {fill.data.tables
                .map(
                  (t) =>
                    `${t.table} (${t.sheet}${t.rows !== undefined ? `, ${t.rows} rows${t.clearedRows ? `, ${t.clearedRows} old rows cleared` : ''}` : ''})`,
                )
                .join('; ')}
              .
            </p>
            {fill.data.warnings.map((w) => (
              <p key={w} className="text-ink bg-[#fff4d6] px-2 py-1 mt-2">
                {w}
              </p>
            ))}
            {fill.data.unfilled.length ? (
              <details className="mt-2">
                <summary className="cursor-pointer">{fill.data.unfilled.length} cells left for you to fill</summary>
                <ul className="mt-2 space-y-1">
                  {fill.data.unfilled.map((u) => (
                    <li key={`${u.table}-${u.ref}`}>
                      {u.table} {u.ref} ({u.row} · {u.column}): {u.reason}
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
          </div>
        ) : null}
      </PanelBody>
    </Panel>
  );
}

function SnapshotPanel({ year }: { year: number }) {
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const list = useQuery<Snapshot[]>(
    ['cihr-snapshots', year],
    async () => (await api.get('/reports/cihr/snapshots', { params: { year } })).data.data,
  );
  const freeze = useMutation(
    async () => {
      const submittedFile = file ? { fileName: file.name, content: await toBase64(file) } : undefined;
      return (
        await api.post(
          `/reports/cihr/${year}/snapshots`,
          { note: note || undefined, submittedFile },
          { timeout: 120000 },
        )
      ).data.data;
    },
    {
      onSuccess: () => {
        setNote('');
        setFile(null);
        queryClient.invalidateQueries(['cihr-snapshots', year]);
      },
    },
  );
  return (
    <Panel>
      <PanelHeader title="Snapshots" note={list.data ? `${list.data.length}` : undefined} />
      <PanelBody>
        <p className="text-table text-ink-2 mb-3">
          Freeze the tables as generated now: values, how each was counted, the code version, and optionally the
          fingerprint (SHA-256) of the file you submitted. Snapshots can never be changed or deleted.
        </p>
        <input
          className={inputClass}
          placeholder="Note (e.g. as submitted to CIHR)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <label className="block text-small text-ink-2 mt-3 mb-1">
          Submitted workbook (optional; only its fingerprint is kept)
        </label>
        <input
          type="file"
          accept=".xlsx"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          className="text-small"
        />
        <div className="mt-3">
          <Button variant="primary" size="sm" disabled={freeze.isLoading} onClick={() => freeze.mutate()}>
            {freeze.isLoading ? 'Freezing…' : 'Freeze snapshot'}
          </Button>
        </div>
        {freeze.error ? (
          <div className="mt-3">
            <ErrorNotice>{errorMessage(freeze.error, 'Could not freeze the report.')}</ErrorNotice>
          </div>
        ) : null}
        <ul className="mt-4 text-small space-y-2">
          {(list.data ?? []).map((s) => (
            <li key={s.id} className="border-b border-line pb-2">
              {dateShort(s.generatedAt)}
              {s.note ? ` · ${s.note}` : ''}
              <div className="text-ink-2 text-small">
                code {s.codeVersion ? s.codeVersion.slice(0, 12) : '—'}
                {s.submittedFileSha256
                  ? ` · ${s.submittedFileName ?? 'file'} ${s.submittedFileSha256.slice(0, 12)}…`
                  : ''}
              </div>
            </li>
          ))}
        </ul>
      </PanelBody>
    </Panel>
  );
}

export default function CihrReport() {
  const { user } = useAuth();
  const admin = canMutate(user?.role);
  const thisYear = new Date().getFullYear();
  const years = Array.from({ length: thisYear - FIRST_YEAR + 1 }, (_, i) => thisYear - i);
  const [year, setYear] = useState(thisYear - 1);
  const [tab, setTab] = useState<TabKey>('B');
  const report = useQuery<Report>(
    ['cihr', year],
    async () => (await api.get(`/reports/cihr/${year}`, { timeout: 120000 })).data.data,
  );
  const csv = useMutation(async (t: TabKey) => {
    const res = await api.get(`/reports/cihr/${year}/csv`, { params: { tab: t }, responseType: 'blob' });
    download(`CIHR ${year} Tab ${t}.csv`, res.data as Blob);
  });
  const tabs: TabKey[] = admin ? ['A', 'B', 'C', 'D'] : ['A', 'B', 'D'];
  const r = report.data;

  return (
    <>
      <PageHeader
        title="CIHR annual report tables"
        sub="HRTP Tables 1a–1c and 2 for a calendar year, generated from what the platform holds. Every number can be explained; cells the platform does not track are left for you."
        aside={
          <Link to="/reports" className="text-small underline whitespace-nowrap">
            All reports
          </Link>
        }
      />
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select className={selectClass} value={year} onChange={(e) => setYear(Number(e.target.value))}>
          {years.map((y) => (
            <option key={y} value={y}>
              {y} (Year {y - FIRST_YEAR + 1})
            </option>
          ))}
        </select>
        {tabs.map((t) => (
          <Button key={t} size="sm" variant={tab === t ? 'primary' : 'ghost'} onClick={() => setTab(t)}>
            {TAB_LABEL[t]}
          </Button>
        ))}
        <Button size="sm" variant="ghost" disabled={csv.isLoading} onClick={() => csv.mutate(tab)}>
          CSV
        </Button>
      </div>
      {!admin ? (
        <div className="mb-4">
          <InfoNotice>
            You see the counts of Tabs A, B and D. Tab C lists cohort participants one by one, so it is for admins.
          </InfoNotice>
        </div>
      ) : null}
      <div className="grid gap-6 items-start grid-cols-1 xl:grid-cols-[1fr_340px]">
        <div className="flex flex-col gap-6 min-w-0">
          <Panel>
            <PanelHeader
              title={
                r ? ((r.tabs[tab] as CountTable | DetailTable | undefined)?.title ?? TAB_LABEL[tab]) : TAB_LABEL[tab]
              }
              note={r ? `${r.yearLabel} · ${r.year}` : undefined}
            />
            <PanelBody>
              {report.isLoading ? (
                <SkeletonRows rows={8} />
              ) : report.error ? (
                <ErrorNotice>{errorMessage(report.error, 'Could not build the report.')}</ErrorNotice>
              ) : r ? (
                tab === 'C' ? (
                  r.tabs.C ? (
                    <DetailView table={r.tabs.C} />
                  ) : null
                ) : (
                  <CountView table={r.tabs[tab]} />
                )
              ) : null}
            </PanelBody>
          </Panel>
          {r ? (
            <Panel>
              <PanelHeader title="Rules and gaps" />
              <PanelBody>
                <ul className="text-small space-y-1">
                  <li>
                    Cohort participants in a year: Fellows within their {r.rules.tenureYears}-year tenure,{' '}
                    {r.rules.cohortRule === 'activity' ? 'active in that year' : 'whether active or not'}.
                  </li>
                  <li>
                    Non-cohort participants: registered members{' '}
                    {r.rules.nonCohortRule === 'activity' ? 'active in the year' : 'from registration on'}; members of{' '}
                    {r.rules.teamGroups.join(', ')} are platform team, not participants.
                  </li>
                  <li>Platform-led learning: attendance from {r.rules.platformLedSources.join(', ')}.</li>
                </ul>
                {r.gaps.length ? (
                  <>
                    <div className="text-small text-ink-2 mt-4 mb-1">Not tracked on the platform</div>
                    <ul className="text-small space-y-1">
                      {r.gaps.map((g) => (
                        <li key={`${g.tab}-${g.row}`} className="bg-[#fff4d6] px-2 py-1">
                          Tab {g.tab} · {g.row}: {g.message}
                        </li>
                      ))}
                    </ul>
                  </>
                ) : null}
                <ul className="text-small text-ink-2 space-y-1 mt-3">
                  {r.notes.map((n) => (
                    <li key={n}>{n}</li>
                  ))}
                </ul>
                {r.unmappedRoles.length ? (
                  <>
                    <div className="text-small text-ink-2 mt-4 mb-1">
                      Career stages not in the role table (counted as Other Trainee)
                    </div>
                    <ul className="text-small space-y-1">
                      {r.unmappedRoles.map((u) => (
                        <li key={u.value}>
                          {u.value}: {u.people}
                        </li>
                      ))}
                    </ul>
                  </>
                ) : null}
              </PanelBody>
            </Panel>
          ) : null}
        </div>
        {admin && r ? (
          <div className="flex flex-col gap-6">
            <TemplatePanel year={r.year} yearLabel={r.yearLabel} />
            <SnapshotPanel year={r.year} />
          </div>
        ) : null}
      </div>
    </>
  );
}
